/**
 * The user-report queue — "this app is slow right now", pressed in the
 * extension's toolbar popup — as the body of `/dynamics/reports`, plus the L0
 * count tile the Dynamics board carries down to it.
 *
 * The section lives here and the page chrome lives in `./reports-page.tsx`, so
 * the queue keeps one owner for its query, its filters and its three states
 * while the page owns the reason gate, the breadcrumbs and the record drawer.
 *
 * ## Why the queue needs no reason, though the page beside it does
 *
 * A report is the submitter's own data, offered on purpose: the click *is* the
 * consent, which is why NOVA may store it at all when D7 forbids every other
 * identity-bearing store. So listing the queue needs no reason — an investigator
 * has to see what arrived before they can say why they are looking.
 *
 * Opening one is different. The detail names a person, their comment, their
 * browser and the F&O loads that matched, which is exactly the per-user view the
 * console gates: the call carries the session's reason and the backend writes an
 * audit row before it reads anything. Hence the drawer refuses to fetch without
 * one, in the same words the record drawer uses.
 *
 * ## Three states, never two
 *
 * `available: false` means the report store could not be read — the queue is
 * *unknown*, and the panel renders the backend's own sentence rather than "no
 * reports". A 403 means this sign-in is not on the investigator list, which is
 * also not an empty queue. Only `available: true` with an empty list is the
 * genuine "nobody complained" state, and it says so in those words.
 *
 * ## Honesty on the numbers
 *
 * `matched: 0` is a finding, not a failure: the user felt something the
 * workspace does not see as a slow form load, and the row says so instead of
 * showing a bare zero. The status counts come from the backend's own `counts`
 * map over the same window, so the tile and the grid cannot disagree.
 */

import { Icon, MessageSquareWarning, UserRound } from "@norican/icons";
import {
  Alert,
  Badge,
  Chip,
  ChipGroup,
  DataGrid,
  type DataGridColumnDef,
  DescriptionList,
  EmptyState,
  Freshness,
  Skeleton,
  Statistic,
  Typography,
} from "@norican/react";

import { DetailDrawer } from "@/components/board";
import {
  type FoConsoleReportLookback,
  type FoConsoleReportStatus,
  type FoConsoleReportSummary,
  useFoConsoleReport,
  useFoConsoleReports,
  useUpdateFoConsoleReport,
} from "@/lib/api";
import { drillTo, useDrawerParam, useDrillParams } from "@/lib/drill";
import { rumAppContextName, rumAppLabel } from "@/lib/rum-apps";

import { AppChip, ClassificationChip, ReportStatusChip } from "./console-chips";
import {
  apiErrorDetail,
  apiErrorStatus,
  DEFAULT_REPORT_LOOKBACK,
  EM_DASH,
  formatDurationMs,
  parseReportLookback,
  parseReportStatus,
  REPORT_LOOKBACKS,
  REPORT_NO_MATCH_LINE,
  REPORT_STATUS_DETAIL,
  REPORT_STATUS_LABEL,
  REPORT_STATUSES,
  reportLookbackOption,
  reportMatchStateDetail,
  reportStatusTransitions,
} from "./console-format";
import { KpiTile } from "./kpi-strip";
import { PanelCard } from "./panel-card";
import {
  DYNAMICS_PATH,
  DYNAMICS_REPORTS_PATH,
  REPORT_PARAM,
  REPORT_STATUS_PARAM,
  REPORT_WINDOW_PARAM,
  REPORTS_ANCHOR,
} from "./params";
import { formatBinDateTime, formatCount } from "./status";

/* ------------------------------------------------------------------------ *
 * URL state
 * ------------------------------------------------------------------------ */

/** The queue's window and status filter, as the URL carries them. */
export function useReportFilters() {
  const drill = useDrillParams(REPORT_WINDOW_PARAM, REPORT_STATUS_PARAM);
  return {
    lookback: parseReportLookback(drill.params[REPORT_WINDOW_PARAM]),
    status: parseReportStatus(drill.params[REPORT_STATUS_PARAM]),
    setLookback: (value: FoConsoleReportLookback) => drill.set({ [REPORT_WINDOW_PARAM]: value }),
    setStatus: (value: FoConsoleReportStatus | null) =>
      drill.set({ [REPORT_STATUS_PARAM]: value ?? null }),
  };
}

/* ------------------------------------------------------------------------ *
 * L0 — the count tile on /dynamics
 * ------------------------------------------------------------------------ */

/**
 * "Open reports" on the Dynamics board: how many people have said an app is
 * slow and nobody has looked yet. The one door from the board to the queue, so
 * it renders in every state — including the two that are not a number.
 *
 * It shares its query key with the section below, so opening the queue from here
 * costs no second read, and the count cannot disagree with the grid.
 */
export function OpenReportsTile() {
  const reports = useFoConsoleReports(DEFAULT_REPORT_LOOKBACK, null);
  const data = reports.data;
  const status = apiErrorStatus(reports.error);
  const option = reportLookbackOption(DEFAULT_REPORT_LOOKBACK);
  // Straight to the queue's own screen, pre-filtered to the untriaged reports
  // the number counts — so the tile's figure and the grid's rows are the same
  // set, not a number that lands on a longer list.
  const to = drillTo(DYNAMICS_REPORTS_PATH, {
    [REPORT_WINDOW_PARAM]: DEFAULT_REPORT_LOOKBACK,
    [REPORT_STATUS_PARAM]: "new",
  });

  // Not on the investigator list: the queue exists, this sign-in may not read
  // it. That is not zero reports, and the tile must not imply a quiet week.
  // Still asking: an em dash with "reading" beats a zero and beats a blank tile.
  if (reports.isPending && !data && !reports.error) {
    return (
      <KpiTile to={to} tone="neutral">
        <Statistic
          label="Open reports"
          value={EM_DASH}
          tone="neutral"
          size="lg"
          caption="Reading the report queue…"
        />
      </KpiTile>
    );
  }

  if (status === 403) {
    return (
      <KpiTile to={DYNAMICS_REPORTS_PATH} tone="neutral">
        <Statistic
          label="Open reports"
          value={EM_DASH}
          tone="neutral"
          size="lg"
          caption="Your sign-in is not on the investigator list, so the report queue was not read. Unknown, not zero."
        />
      </KpiTile>
    );
  }

  if (reports.error || (data && !data.available)) {
    return (
      <KpiTile to={to} tone="neutral">
        <Statistic
          label="Open reports"
          value={EM_DASH}
          tone="neutral"
          size="lg"
          caption={
            data?.detail ??
            "The report store could not be read, so the queue length is unknown — not zero."
          }
        />
      </KpiTile>
    );
  }

  const open = data?.counts.new ?? null;
  const triaged = data?.counts.triaged ?? null;

  return (
    <KpiTile to={to} tone={open != null && open > 0 ? "warning" : "neutral"}>
      <Statistic
        label="Open reports"
        value={formatCount(open)}
        tone={open != null && open > 0 ? "warning" : "neutral"}
        size="lg"
        caption={
          open == null
            ? `The queue was not counted for the ${option.label} window — unknown, not zero`
            : `${formatCount(triaged)} in triage · ${option.caption} · people who pressed "this app is slow right now"`
        }
      />
    </KpiTile>
  );
}

/* ------------------------------------------------------------------------ *
 * L2 — the queue and one report
 * ------------------------------------------------------------------------ */

interface ReportsSectionProps {
  /** The session's reason — needed to open a report, not to list the queue. */
  reason: string | null;
  /** Open the §5 record drawer for an activity id, closing this drawer. */
  onOpenRecord: (activityId: string) => void;
}

export function ReportsSection({ reason, onOpenRecord }: ReportsSectionProps) {
  const filters = useReportFilters();
  const reports = useFoConsoleReports(filters.lookback, filters.status);
  const drawer = useDrawerParam(REPORT_PARAM);
  const data = reports.data;
  const status = apiErrorStatus(reports.error);
  const option = reportLookbackOption(filters.lookback);
  const openId = drawer.value != null && /^\d+$/.test(drawer.value) ? Number(drawer.value) : null;

  const counts = data?.counts ?? {};
  const countCaption = REPORT_STATUSES.map(
    (state) => `${formatCount(counts[state] ?? null)} ${REPORT_STATUS_LABEL[state]}`,
  ).join(" · ");
  const hasRows = data?.available === true && data.reports.length > 0;

  return (
    <section id={REPORTS_ANCHOR} className="flex scroll-mt-6 flex-col gap-4">
      {/* The filter row sits above the card it scopes (design rules §4.3), not
          inside it, so a full-bleed grid can drop the card's body padding
          without the chips losing theirs. */}
      <ReportFilterRow filters={filters} />

      {status === 403 && (
        <Alert tone="warning" title="This sign-in is not on the investigator list">
          {apiErrorDetail(reports.error) ??
            "The report queue is open to the investigators named in NOVA's configuration only. Reports keep arriving; they are simply not readable here."}
        </Alert>
      )}
      {reports.error && status !== 403 && (
        <Alert tone="danger" title="Couldn't read the report queue">
          {status === 0
            ? "The NOVA API did not respond. This is a NOVA problem — the reports themselves are in the store."
            : (apiErrorDetail(reports.error) ??
              `The NOVA API answered ${status ?? "with an error"}.`)}
        </Alert>
      )}

      <PanelCard
        title="Reports from users"
        caption={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{option.caption}</span>
            {data?.available && <span className="tabular-nums">{countCaption}</span>}
            {reports.dataUpdatedAt > 0 && (
              <Freshness
                label="Queue read"
                since={reports.dataUpdatedAt}
                pulse={reports.isFetching}
              />
            )}
          </span>
        }
        padding={hasRows ? "none" : "md"}
      >
        {reports.isPending && !data && !reports.error && <Skeleton height="12rem" />}

        {data && !data.available && (
          <EmptyState
            size="sm"
            title="The report queue could not be read"
            description={data.detail}
          />
        )}

        {data?.available && data.reports.length === 0 && (
          <EmptyState
            size="sm"
            icon={<Icon icon={MessageSquareWarning} size="lg" aria-hidden="true" />}
            title={
              filters.status
                ? `No ${REPORT_STATUS_LABEL[filters.status]} report in this window`
                : "Nobody reported an app as slow in this window"
            }
            description={
              filters.status
                ? "The store answered; this filter matches nothing. Clear the status filter to see the rest of the queue."
                : "The store answered and holds no report for the window. A genuine zero — people were not asked to stay quiet, they simply did not press the button."
            }
          />
        )}

        {hasRows && <ReportsGrid rows={data.reports} onOpen={(id) => drawer.open(String(id))} />}

        {/* No grid renders without a payload: an empty grid would be the
            flattering reading of a failed query. The card still says something,
            so it is never a blank panel under an alert. */}
        {!data && !reports.isPending && (
          <EmptyState
            size="sm"
            title={reports.error ? "The queue length is unknown" : "The queue was not read"}
            description={
              reports.error
                ? "The call above failed, so nothing is claimed about how many people have reported. Not zero."
                : "The panel asked for nothing, so nothing is claimed about how many people have reported."
            }
          />
        )}
      </PanelCard>

      <ReportDrawer
        id={openId}
        reason={reason}
        onClose={drawer.close}
        onOpenRecord={(activityId) => {
          drawer.close();
          onOpenRecord(activityId);
        }}
      />
    </section>
  );
}

function ReportFilterRow({ filters }: { filters: ReturnType<typeof useReportFilters> }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="caption" tone="muted">
          Window
        </Typography>
        <ChipGroup.Root
          aria-label="Report queue window"
          value={[filters.lookback]}
          onValueChange={(value) => {
            const next = value[0];
            if (next) {
              filters.setLookback(parseReportLookback(next));
            }
          }}
        >
          {REPORT_LOOKBACKS.map((entry) => (
            <Chip key={entry.id} value={entry.id} size="sm">
              {entry.label}
            </Chip>
          ))}
        </ChipGroup.Root>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="caption" tone="muted">
          Status
        </Typography>
        <ChipGroup.Root
          aria-label="Report status filter"
          value={filters.status ? [filters.status] : []}
          onValueChange={(value) => filters.setStatus(parseReportStatus(value[0]))}
        >
          {REPORT_STATUSES.map((state) => (
            <Chip key={state} value={state} size="sm" title={REPORT_STATUS_DETAIL[state]}>
              {REPORT_STATUS_LABEL[state]}
            </Chip>
          ))}
        </ChipGroup.Root>
        <Typography variant="caption" tone="muted">
          {filters.status
            ? "click the selected chip again for every status"
            : "every status — the counts above are the whole window"}
        </Typography>
      </div>
    </div>
  );
}

interface ReportsGridProps {
  rows: FoConsoleReportSummary[];
  onOpen: (id: number) => void;
}

function ReportsGrid({ rows, onOpen }: ReportsGridProps) {
  const columns: DataGridColumnDef<FoConsoleReportSummary>[] = [
    {
      id: "ts",
      header: "Reported",
      accessor: (row) => row.ts,
      sortable: true,
      cell: (row) => (
        <button
          type="button"
          className="text-fg rounded text-left font-medium tabular-nums hover:underline focus-visible:outline-2"
          onClick={() => onOpen(row.id)}
          aria-label={`Open report ${row.id} from ${formatBinDateTime(row.ts)}`}
        >
          {formatBinDateTime(row.ts)}
        </button>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessor: (row) => row.status,
      sortable: true,
      cell: (row) => <ReportStatusChip status={row.status} />,
    },
    {
      id: "app",
      header: "App",
      accessor: (row) => row.app,
      sortable: true,
      cell: (row) => <AppChip app={row.app} short />,
    },
    {
      id: "context",
      header: "Page",
      accessor: (row) => row.context,
      sortable: true,
      cell: (row) =>
        row.context ? (
          <code className="text-xs break-all">{row.context}</code>
        ) : (
          <span
            className="text-fg-subtle text-xs"
            title={
              rumAppContextName(row.app) == null
                ? "no page key is collected for this app"
                : "the popup carried no page key"
            }
          >
            {rumAppContextName(row.app) == null ? "not collected" : EM_DASH}
          </span>
        ),
    },
    {
      id: "who",
      header: "Who",
      accessor: (row) => row.display_name || row.mail,
      sortable: true,
      cell: (row) => (
        <span className="flex flex-col">
          <span className="text-fg text-sm">{row.display_name || "name not resolved"}</span>
          <span className="text-fg-muted text-xs break-all">{row.mail}</span>
        </span>
      ),
    },
    {
      id: "comment",
      header: "What they wrote",
      accessor: (row) => row.comment,
      cell: (row) =>
        row.comment ? (
          <span className="line-clamp-2 text-sm" title={row.comment}>
            {row.comment}
          </span>
        ) : (
          <span className="text-fg-subtle text-xs">no comment — the click alone</span>
        ),
    },
    {
      id: "matched",
      header: "F&O loads matched",
      align: "end",
      accessor: (row) => row.matched,
      sortable: true,
      cell: (row) =>
        row.matched === 0 ? (
          <Badge tone="neutral" variant="outline" size="xs" title={REPORT_NO_MATCH_LINE}>
            none matched
          </Badge>
        ) : (
          <span className="tabular-nums">{formatCount(row.matched)}</span>
        ),
    },
    {
      id: "classification",
      header: "Classification",
      accessor: (row) => row.classification,
      sortable: true,
      cell: (row) =>
        row.classification ? (
          <ClassificationChip classification={row.classification} />
        ) : (
          <span className="text-fg-subtle text-xs">not classified</span>
        ),
    },
  ];

  return (
    <div className="h-[26rem]">
      {/* DataGrid needs a definite height for its sticky header and scroller. */}
      <DataGrid<FoConsoleReportSummary>
        data={rows}
        getRowId={(row) => String(row.id)}
        density="compact"
        aria-label="User reports of application slowness"
        columns={columns}
      />
    </div>
  );
}

interface ReportDrawerProps {
  id: number | null;
  reason: string | null;
  onClose: () => void;
  onOpenRecord: (activityId: string) => void;
}

/**
 * One report in full. This is a per-user view (D7): the fetch carries the
 * session's reason and the backend audit-logs it, so with no reason the drawer
 * says so and asks for nothing.
 */
function ReportDrawer({ id, reason, onClose, onOpenRecord }: ReportDrawerProps) {
  const report = useFoConsoleReport(id, reason);
  const data = report.data;
  const status = apiErrorStatus(report.error);
  const update = useUpdateFoConsoleReport();

  return (
    <DetailDrawer
      open={id != null}
      onClose={onClose}
      title={data ? `Report ${data.id} · ${rumAppLabel(data.app)}` : id != null ? "Report" : ""}
      breadcrumbs={[
        { label: "Dynamics", to: DYNAMICS_PATH },
        { label: "Reports", to: DYNAMICS_REPORTS_PATH },
      ]}
    >
      {!reason && (
        <Alert tone="warning" title="No reason for this session">
          A report names the person who sent it, so opening one is a per-user view. Enter a reason
          on the page behind this drawer; the backend refuses the call without one.
        </Alert>
      )}

      {report.error && status === 400 && (
        <Alert tone="warning" title="The backend refused the call: reason missing or too short">
          {apiErrorDetail(report.error) ??
            "Every per-user call carries a reason of at least four characters."}
        </Alert>
      )}
      {report.error && status === 404 && (
        <Alert tone="neutral" title="No report with this id">
          {apiErrorDetail(report.error) ??
            "Either the id is wrong, or the report has aged past the store's 90-day purge."}
        </Alert>
      )}
      {report.error && status !== 400 && status !== 404 && (
        <Alert tone="danger" title="Couldn't load the report">
          {status === 0
            ? "The NOVA API did not respond."
            : (apiErrorDetail(report.error) ??
              `The NOVA API answered ${status ?? "with an error"}.`)}
        </Alert>
      )}

      {report.isPending && id != null && Boolean(reason) && !data && (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton height="6rem" />
          <Skeleton height="8rem" />
        </div>
      )}

      {data && !data.available && (
        <EmptyState
          size="sm"
          title="The report could not be read in full"
          description={data.detail}
        />
      )}

      {data?.available && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <ReportStatusChip status={data.status} size="sm" />
            <AppChip app={data.app} size="sm" />
            {data.classification && (
              <ClassificationChip classification={data.classification} size="sm" />
            )}
          </div>

          <DescriptionList
            columns={1}
            items={[
              { label: "Reported", value: formatBinDateTime(data.ts) },
              {
                label: "Who",
                value: (
                  <span className="inline-flex items-center gap-2">
                    <Icon icon={UserRound} size="xs" aria-hidden="true" />
                    {data.display_name || "name not resolved"}
                    <span className="text-fg-muted break-all">{data.mail}</span>
                  </span>
                ),
              },
              {
                label: "Page",
                value: data.context ? (
                  <code className="text-xs break-all">{data.context}</code>
                ) : (
                  (rumAppContextName(data.app) ?? "no page key is collected for this app")
                ),
              },
              { label: "Legal entity", value: data.legal_entity || EM_DASH },
              {
                label: "What they wrote",
                value: data.comment || "nothing — the click alone was the report",
              },
            ]}
          />

          <StatusControl
            current={data.status}
            pending={update.isPending}
            onChange={(next) => update.mutate({ id: data.id, status: next })}
          />

          <section className="flex flex-col gap-2">
            <Typography
              variant="caption"
              tone="muted"
              as="h3"
              className="font-semibold tracking-wide uppercase"
            >
              F&O loads matched
            </Typography>
            {data.matches.length === 0 ? (
              <Typography variant="body-sm">{REPORT_NO_MATCH_LINE}</Typography>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.matches.map((match) => (
                  <li
                    key={match.activity_id}
                    className="border-border-subtle flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded border px-3 py-2"
                  >
                    <button
                      type="button"
                      className="text-fg rounded text-sm font-medium tabular-nums hover:underline focus-visible:outline-2"
                      onClick={() => onOpenRecord(match.activity_id)}
                      aria-label={`Open the record for ${match.form} at ${formatBinDateTime(match.ts)}`}
                    >
                      {formatBinDateTime(match.ts)}
                    </button>
                    <span className="text-sm">{match.form}</span>
                    <span className="text-fg-muted text-xs">{match.legal_entity}</span>
                    <span className="text-sm tabular-nums">
                      {formatDurationMs(match.duration_ms)}
                    </span>
                    {match.slow ? (
                      <Badge tone="warning" variant="subtle" size="xs">
                        slow by the §5 rule
                      </Badge>
                    ) : (
                      <Badge tone="neutral" variant="outline" size="xs">
                        not slow by the §5 rule
                      </Badge>
                    )}
                    <Badge tone="neutral" variant="outline" size="xs">
                      {match.via === "activity" ? "matched by request id" : "matched in ±30 s"}
                    </Badge>
                    {match.classification && (
                      <ClassificationChip classification={match.classification} />
                    )}
                  </li>
                ))}
              </ul>
            )}
            <Typography variant="caption" tone="muted">
              {reportMatchStateDetail(data.match_state)}
            </Typography>
          </section>

          <section className="flex flex-col gap-2">
            <Typography
              variant="caption"
              tone="muted"
              as="h3"
              className="font-semibold tracking-wide uppercase"
            >
              Request ids the browser attached
            </Typography>
            {data.matched_activity_ids.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                None. Either the app sent no correlation header on the requests in that minute, or
                the popup carried no recent minute — so the match fell back to the ±30 s window.
              </Typography>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.matched_activity_ids.map((activityId) => (
                  <button
                    key={activityId}
                    type="button"
                    className="border-border-subtle text-fg-muted hover:text-fg rounded border px-2 py-1 font-mono text-xs focus-visible:outline-2"
                    onClick={() => onOpenRecord(activityId)}
                    aria-label={`Open the record for activity ${activityId}`}
                  >
                    {activityId}
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <Typography
              variant="caption"
              tone="muted"
              as="h3"
              className="font-semibold tracking-wide uppercase"
            >
              The browser that reported
            </Typography>
            {data.client ? (
              <DescriptionList
                columns={2}
                items={[
                  { label: "Browser", value: data.client.browser || EM_DASH },
                  { label: "Platform", value: data.client.platform || EM_DASH },
                  {
                    label: "Cores · memory",
                    value: (
                      <span className="tabular-nums">
                        {formatCount(data.client.cores ?? null)} ·{" "}
                        {data.client.memory_gb == null ? EM_DASH : `${data.client.memory_gb} GB`}
                      </span>
                    ),
                  },
                  {
                    label: "Connection",
                    value: (
                      <span className="tabular-nums">
                        {data.client.conn_type || EM_DASH} · RTT{" "}
                        {formatDurationMs(data.client.rtt_ms)}
                      </span>
                    ),
                  },
                ]}
              />
            ) : (
              <Typography variant="body-sm" tone="muted">
                The popup sent no browser block with this report.
              </Typography>
            )}
            <Typography variant="caption" tone="muted">
              {formatCount(data.recent.length)} of the submitter&apos;s own last browser minutes
              travelled with the report, which is what the request-id match ran against. No
              user-agent string, no device name.
            </Typography>
          </section>
        </div>
      )}
    </DetailDrawer>
  );
}

interface StatusControlProps {
  current: FoConsoleReportStatus;
  pending: boolean;
  onChange: (next: FoConsoleReportStatus) => void;
}

/**
 * Move the report through the queue. Only the moves
 * `reportStatusTransitions()` allows are offered — reopening a closed report
 * lands in `triaged`, not back in `new`, because it has already been read and
 * calling it new again would misdescribe the queue to whoever picks it up.
 */
function StatusControl({ current, pending, onChange }: StatusControlProps) {
  const allowed = reportStatusTransitions(current);
  return (
    <div className="flex flex-col gap-1">
      <Typography
        variant="caption"
        tone="muted"
        as="h3"
        className="font-semibold tracking-wide uppercase"
      >
        Status
      </Typography>
      <div className="flex flex-wrap items-center gap-2">
        <ChipGroup.Root
          aria-label="Report status"
          value={[current]}
          onValueChange={(value) => {
            const next = parseReportStatus(value[0]);
            if (next && next !== current && allowed.includes(next)) {
              onChange(next);
            }
          }}
        >
          {REPORT_STATUSES.map((state) => (
            <Chip
              key={state}
              value={state}
              size="sm"
              disabled={pending || (state !== current && !allowed.includes(state))}
              title={REPORT_STATUS_DETAIL[state]}
            >
              {REPORT_STATUS_LABEL[state]}
            </Chip>
          ))}
        </ChipGroup.Root>
        <Typography variant="caption" tone="muted">
          {pending ? "saving…" : REPORT_STATUS_DETAIL[current]}
        </Typography>
      </div>
    </div>
  );
}
